/**
 * @file publish-acknowledge.ts
 * @description Публикация вида сайта с незакрытыми проверками (phase19.16,
 * SB125; уточняет SB59, SB74, SB34/SB35): тексты галочки «На сайте нет: …
 * отвечаю за это я» и постоянной плашки «Сайт работает без: …».
 *
 * Чего не хватает — реестром «код проверки → что именно» (OCP): новая
 * блокирующая проверка, которую стоит назвать по-человечески, — запись в
 * `MISSING_BY_CODE`. Коды, которых в реестре нет, показываются текстом
 * проверки с сервера («Не исправлено: …»).
 */

import { isShopLevelCheckCode } from "@chaiberi/shared";

import type { PublishCheck } from "./types";

/** Чего нет на сайте: полная форма (галочка) и короткая (плашка). */
export interface MissingItem {
  key: string;
  /** «политики обработки персональных данных» — в тексте галочки. */
  full: string;
  /** «политики ПДн» — в плашке кабинета. */
  short: string;
}

const SELLER: MissingItem = { key: "seller", full: "реквизитов продавца", short: "реквизитов продавца" };
const OFFER: MissingItem = { key: "offer", full: "публичной оферты", short: "оферты" };
const PRIVACY: MissingItem = {
  key: "privacy",
  full: "политики обработки персональных данных",
  short: "политики ПДн",
};
const DOCS: MissingItem = { key: "docs", full: "правовых документов", short: "правовых документов" };
const PRODUCT: MissingItem = {
  key: "product",
  full: "обязательных сведений о товаре",
  short: "обязательных сведений о товаре",
};

/**
 * Документы: сервер перечисляет отсутствующие в тексте проверки
 * («Опубликуйте документы: публичная оферта, политика обработки
 * персональных данных…») — разбираем по подстрокам.
 */
function legalDocsItems(message: string): MissingItem[] {
  const text = message.toLowerCase();
  const items: MissingItem[] = [];
  if (text.includes("оферт")) items.push(OFFER);
  if (text.includes("персональных данных") || text.includes("пдн")) items.push(PRIVACY);
  return items.length > 0 ? items : [DOCS];
}

/** Реестр: код блокирующей проверки → чего нет на сайте. */
const MISSING_BY_CODE: Readonly<Record<string, (check: PublishCheck) => MissingItem[]>> = {
  site_view_seller_incomplete: () => [SELLER],
  site_view_legal_docs_missing: (check) => legalDocsItems(check.message),
  site_product_template_invalid: () => [PRODUCT],
};

/** Разбор ошибок: что названо по реестру и тексты остальных проверок. */
export interface AcknowledgeParts {
  missing: MissingItem[];
  /** Тексты проверок, которых нет в реестре (без точки в конце). */
  other: string[];
}

/** Только проверки уровня error, без повторов. */
export function acknowledgeParts(checks: readonly PublishCheck[]): AcknowledgeParts {
  const missing: MissingItem[] = [];
  const other: string[] = [];
  for (const check of checks) {
    if (check.level !== "error") continue;
    const byCode = MISSING_BY_CODE[check.code];
    if (byCode) {
      for (const item of byCode(check)) if (!missing.some((m) => m.key === item.key)) missing.push(item);
      continue;
    }
    const text = check.message.trim().replace(/[.\s]+$/u, "");
    if (text && !other.includes(text)) other.push(text);
  }
  return { missing, other };
}

/** «Не исправлено: …» — проверки без названия в реестре. */
function otherSentence(other: readonly string[]): string {
  return other.length > 0 ? `Не исправлено: ${other.join("; ")}.` : "";
}

/**
 * Текст галочки окна публикации (SB125 п. 1). Закон упоминаем, только когда
 * на сайте нет того, чего требует закон (реквизиты, документы, сведения о
 * товаре); иначе — общее подтверждение.
 */
export function acknowledgeText(checks: readonly PublishCheck[]): string {
  const { missing, other } = acknowledgeParts(checks);
  const sentences: string[] = [];
  if (missing.length > 0) sentences.push(`На сайте нет: ${missing.map((m) => m.full).join(", ")}.`);
  if (other.length > 0) sentences.push(otherSentence(other));
  sentences.push(
    missing.length > 0
      ? "Понимаю, что это нарушает закон о защите прав потребителей и о персональных данных, и всё равно публикую — отвечаю за это я"
      : "Понимаю, что сайт будет работать с незакрытыми проверками, и всё равно публикую — отвечаю за это я",
  );
  return sentences.join(" ");
}

/**
 * Текст плашки кабинета (SB125 п. 5): только ошибки по данным магазина
 * (`SHOP_LEVEL_CHECK_CODES` в shared live-checks.ts — один список с сервером
 * `live-issues`, SB120 этап 2); таких нет — пустая строка.
 */
export function liveWithoutText(checks: readonly PublishCheck[]): string {
  const { missing } = acknowledgeParts(checks.filter((c) => isShopLevelCheckCode(c.code)));
  return missing.length > 0 ? `Сайт работает без: ${missing.map((m) => m.short).join(", ")}.` : "";
}

/** Ошибки по данным магазина из любого списка проверок (плашка конструктора и «Сводки»). */
export function shopLevelErrors(checks: readonly PublishCheck[] | undefined): PublishCheck[] {
  return (checks ?? []).filter((c) => c.level === "error" && isShopLevelCheckCode(c.code));
}
